// src/pages/heirs/RegisterHeir.jsx
// Page d'inscription pour un bénéficiaire (héritier) invité via token, sur le modèle de
// pages/collaborators/RegisterCollaborator.jsx.
//
// Flux "nouveau compte" : token dans l'URL → get_beneficiary_invite_info (pré-remplissage)
// → création du compte Supabase avec beneficiary_token dans les métadonnées. Le
// rattachement (role="heir", beneficiaries.user_id, beneficiary_invites.status="accepted")
// est fait automatiquement côté serveur par le trigger handle_new_user() au moment de
// l'INSERT dans auth.users.
//
// Flux "compte déjà existant" (héritier déjà bénéficiaire d'un autre collectionneur, ou
// déjà client Artkiv) : le trigger handle_new_user() ne se déclenche jamais pour un compte
// existant, donc on appelle explicitement la RPC accept_beneficiary_invite une fois
// authentifié — cf. le même schéma que src/pages/InvitationNotaire.jsx pour
// accept_notaire_invitation. Si l'utilisateur n'est pas connecté, on le renvoie vers
// /login?redirect=<cette page + token> pour revenir ici automatiquement après connexion.
//
// Cette page ne crée aucun accès à une collection ou à des œuvres : la création du compte
// et le partage d'une collection précise restent deux actions distinctes, à la discrétion
// du collectionneur (voir collection_permissions).

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

const VISION_BASE = process.env.REACT_APP_VISION_URL || "https://vision.artkiv.com";

export default function RegisterHeir() {
  const [params] = useSearchParams();
  const token = params.get("token");

  // loading | invalid | form | submitting | done | confirm_email | error
  // | already_authenticated | accepting | accepted
  const [step, setStep] = useState("loading");
  const [inviteInfo, setInviteInfo] = useState(null); // { email, name, relation, owner_name }
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);

  const loadInvite = useCallback(async () => {
    if (!token) {
      setStep("invalid");
      return;
    }

    const [{ data, error }, { data: sessionData }] = await Promise.all([
      supabase.rpc("get_beneficiary_invite_info", { p_token: token }),
      supabase.auth.getSession(),
    ]);

    if (error) {
      console.error("[RegisterHeir] RPC error:", error);
      setStep("invalid");
      return;
    }

    if (!data?.valid) {
      setStep("invalid");
      return;
    }

    setInviteInfo({
      email: data.email,
      name: data.name,
      relation: data.relation,
      ownerName: data.owner_name,
    });

    // Déjà connecté (compte pré-existant, ou retour depuis /login après le
    // clic "Connectez-vous" plus bas) : on saute le formulaire de création
    // de compte, l'acceptation se fait via accept_beneficiary_invite.
    if (sessionData?.session) {
      setStep("already_authenticated");
      return;
    }

    setForm((f) => ({ ...f, email: data.email || "" }));
    setStep("form");
  }, [token]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  const acceptAsExistingAccount = async () => {
    setStep("accepting");
    setErrorMsg("");

    const { data, error } = await supabase.rpc("accept_beneficiary_invite", { p_token: token });

    if (error || !data?.ok) {
      const code = data?.error || "unknown_error";
      const messages = {
        email_mismatch:
          "Cette invitation a été envoyée à une autre adresse email que celle de votre compte connecté. Déconnectez-vous puis réessayez avec le bon compte.",
        already_claimed_by_another_account:
          "Cette invitation a déjà été acceptée par un autre compte.",
        already_accepted: "Cette invitation a déjà été acceptée.",
        already_revoked: "Cette invitation a été révoquée.",
        expired: "Cette invitation a expiré. Demandez à la personne qui vous a invité de vous en renvoyer une.",
        not_found: "Ce lien d'invitation est invalide.",
      };
      setErrorMsg(messages[code] || "L'acceptation a échoué. Réessayez.");
      setStep("already_authenticated");
      return;
    }

    setStep("accepted");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (form.password !== form.confirmPassword) {
      setErrorMsg("Les mots de passe ne correspondent pas.");
      return;
    }
    if (form.password.length < 6) {
      setErrorMsg("Le mot de passe doit faire au moins 6 caractères.");
      return;
    }

    setStep("submitting");

    const { error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          beneficiary_token: token, // lu par handle_new_user() côté Supabase
        },
      },
    });

    if (signUpError) {
      if (signUpError.message?.toLowerCase().includes("already")) {
        setAlreadyHasAccount(true);
        setErrorMsg("Un compte existe déjà avec cet email. Connectez-vous pour continuer.");
        setStep("form");
        return;
      }
      setErrorMsg(signUpError.message || "Erreur lors de la création du compte.");
      setStep("form");
      return;
    }

    // Selon la config Supabase (confirmation email requise ou non).
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      setStep("done");
    } else {
      setStep("confirm_email");
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    background: "#fafaf8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  };

  const cardStyle = {
    background: "#fff",
    border: "1px solid #e5e3df",
    padding: "40px 40px 36px",
    maxWidth: 480,
    width: "100%",
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#4a4a4a",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e3df",
    borderRadius: 1,
    fontSize: 14,
    color: "#1a1a1a",
    background: "#fafaf8",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "system-ui, sans-serif",
  };

  const btnPrimary = {
    width: "100%",
    padding: "13px 0",
    background: "#1a1a1a",
    color: "#fff",
    border: "none",
    borderRadius: 1,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.04em",
    fontFamily: "system-ui, sans-serif",
    marginTop: 8,
  };

  if (step === "loading") {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: "Georgia, serif", color: "#6b6b6b", fontSize: 16 }}>
          Validation de votre invitation…
        </div>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 12 }}>
            Invitation invalide
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 24 }}>
            Ce lien d'invitation est invalide, expiré ou a déjà été utilisé. Demandez à la personne qui vous a invité de renvoyer le lien.
          </p>
          <Link to="/login" style={{ fontSize: 13, color: "#c9a961" }}>→ Se connecter</Link>
        </div>
      </div>
    );
  }

  if (step === "submitting") {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: "Georgia, serif", color: "#6b6b6b", fontSize: 16 }}>
          Création de votre accès en cours…
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 10 }}>
            Accès activé
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 20 }}>
            Bienvenue sur Artkiv. Votre compte est prêt.
          </p>
          <a
            href={`${VISION_BASE}/matching/heritier`}
            style={{ ...btnPrimary, display: "block", textAlign: "center", textDecoration: "none" }}
          >
            Découvrir la collection
          </a>
        </div>
      </div>
    );
  }

  if (step === "already_authenticated") {
    const loginRedirect = `/login?redirect=${encodeURIComponent(`/heirs/register?token=${token}`)}`;
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 12 }}>
            Invitation héritier
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 20 }}>
            <strong>{inviteInfo?.ownerName}</strong> vous invite à accéder à sa collection
            {inviteInfo?.relation ? ` en tant que ${inviteInfo.relation.toLowerCase()}` : ""}.
            Vous êtes déjà connecté avec un compte Artkiv — acceptez pour rattacher cette
            invitation à votre compte.
          </p>
          {errorMsg && (
            <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", fontSize: 13, color: "#b00020", lineHeight: 1.5, marginBottom: 16 }}>
              {errorMsg}
            </div>
          )}
          <button onClick={acceptAsExistingAccount} style={btnPrimary}>
            Accepter l'invitation
          </button>
          <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#6b6b6b" }}>
            Ce n'est pas vous ?{" "}
            <Link to={loginRedirect} style={{ color: "#1a1a1a", fontWeight: 600 }}>
              Changer de compte →
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (step === "accepting") {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: "Georgia, serif", color: "#6b6b6b", fontSize: 16 }}>
          Rattachement de l'invitation…
        </div>
      </div>
    );
  }

  if (step === "accepted") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 10 }}>
            Invitation acceptée
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 20 }}>
            Vous pouvez désormais découvrir la collection et donner votre avis sur les œuvres.
          </p>
          <a
            href={`${VISION_BASE}/matching/heritier`}
            style={{ ...btnPrimary, display: "block", textAlign: "center", textDecoration: "none" }}
          >
            Découvrir la collection
          </a>
        </div>
      </div>
    );
  }

  if (step === "confirm_email") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 12 }}>
            Confirmez votre email
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7 }}>
            Un email de confirmation a été envoyé à <strong>{form.email}</strong>. Cliquez sur le lien dans cet email pour activer votre accès.
          </p>
        </div>
      </div>
    );
  }

  const loginRedirect = `/login?redirect=${encodeURIComponent(`/heirs/register?token=${token}`)}`;

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: 28, textAlign: "center" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a1a1a", letterSpacing: "0.12em", fontWeight: 400 }}>
            ARTKIV
          </span>
        </Link>
      </div>

      <div style={cardStyle}>
        <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 24, color: "#1a1a1a", margin: "0 0 8px" }}>
          Votre espace héritier
        </h1>
        <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "0 0 28px" }}>
          <strong>{inviteInfo?.ownerName}</strong> vous invite à créer votre accès Artkiv
          {inviteInfo?.relation ? ` en tant que ${inviteInfo.relation.toLowerCase()}` : ""}.
        </p>

        {alreadyHasAccount && (
          <div style={{ padding: "12px 16px", background: "#fef3c7", border: "1px solid #fcd34d", marginBottom: 20, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
            Un compte existe déjà avec cette adresse.{" "}
            <Link to={loginRedirect} style={{ color: "#92400e", fontWeight: 600, textDecoration: "underline" }}>
              Connectez-vous →
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Adresse email *</label>
            <input
              style={{ ...inputStyle, background: inviteInfo?.email ? "#f5f5f5" : "#fafaf8", color: inviteInfo?.email ? "#6b6b6b" : "#1a1a1a" }}
              type="email"
              value={form.email}
              onChange={(e) => !inviteInfo?.email && setForm((f) => ({ ...f, email: e.target.value }))}
              readOnly={!!inviteInfo?.email}
              required
              placeholder="votre@email.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label style={labelStyle}>Mot de passe *</label>
            <input
              style={inputStyle}
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              required
              placeholder="Minimum 6 caractères"
              autoComplete="new-password"
            />
          </div>

          <div>
            <label style={labelStyle}>Confirmer le mot de passe *</label>
            <input
              style={inputStyle}
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              required
              placeholder="Répétez le mot de passe"
              autoComplete="new-password"
            />
          </div>

          {errorMsg && !alreadyHasAccount && (
            <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", fontSize: 13, color: "#b00020", lineHeight: 1.5 }}>
              {errorMsg}
            </div>
          )}

          <p style={{ fontSize: 11, color: "#9a8c7e", lineHeight: 1.6, margin: 0 }}>
            En créant votre accès, vous acceptez les{" "}
            <Link to="/legal" style={{ color: "#9a8c7e" }}>Mentions légales</Link>{" "}
            et la{" "}
            <Link to="/privacy-policy" style={{ color: "#9a8c7e" }}>Politique de confidentialité</Link>{" "}
            d'Artkiv.
          </p>

          <button type="submit" style={btnPrimary}>
            Créer mon accès
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#6b6b6b" }}>
          Déjà un compte ?{" "}
          <Link to={loginRedirect} style={{ color: "#1a1a1a", fontWeight: 600 }}>
            Se connecter →
          </Link>
        </p>
      </div>
    </div>
  );
}
